import * as path from "node:path";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { getAzAccount } from "../../lib/auth.js";
import { readAgentYaml } from "../../lib/agent-yaml.js";
import { resolveRepoRoot } from "../../lib/brain-paths.js";
import { resolveBridgeUrl } from "../../lib/bridge-url.js";
import { enableA2a } from "../../lib/a2a-enable.js";

export class A2aEnableCommand extends M8tCommand {
  static paths = [["a2a", "enable"]];
  static usage = Command.Usage({
    description: "Enable a worker for agent-to-agent delegation (caller + callee). Idempotent (rotates the bearer).",
    details:
      "Attaches the A2A tool[type:mcp] (discover_workers + invoke_worker), provisions a CustomKeys connection holding a freshly-minted bearer, and projects the persona's a2a-card + sha256(bearer) into Foundry agent metadata. The bridge then lists this worker in discover_workers and recognizes it as a caller by the bearer hash.",
    examples: [["Enable the cmo persona", "$0 a2a enable cmo --persona cmo --gateway-url https://<gateway-fqdn>"]],
  });

  worker = Option.String();
  persona = Option.String("--persona");
  gatewayUrl = Option.String("--gateway-url");
  endpoint = Option.String("--endpoint");
  subscription = Option.String("--subscription");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const env = this.context.env as NodeJS.ProcessEnv;
    const outputFlag = this.output === "json" || this.output === "auto" || this.output === "pretty" ? (this.output) : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);
    const progress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId = (typeof this.subscription === "string" ? this.subscription : undefined) ?? account.subscriptionId;
    const agentEndpoint = readAgentYaml(this.worker)?.projectEndpoint;
    const project = await resolveFoundryProject({
      credential, subscriptionId,
      interactive: (this.context.stdout as NodeJS.WriteStream).isTTY === true,
      endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
      agentEndpoint,
    });

    const personaPath = this.resolvePersonaPath();
    const bridgeUrl = resolveBridgeUrl({ flag: typeof this.gatewayUrl === "string" ? this.gatewayUrl : undefined, env });

    const result = await enableA2a({
      credential,
      projectEndpoint: project.endpoint,
      projectArmId: `${project.accountScope}/projects/${project.projectName}`,
      agentName: this.worker,
      personaPath,
      bridgeUrl,
      onProgress: progress,
    });

    if (mode === "json") {
      this.context.stdout.write(renderJson({ worker: this.worker, mode: result.mode, connectionName: result.connectionName, foundryVersion: result.foundryVersion, bridgeUrl }) + "\n");
      return 0;
    }
    if (result.mode === "target") {
      this.context.stdout.write(`${colors.success("✓")} ${colors.field(this.worker)} is a discoverable a2a target (agent version ${result.foundryVersion}).\n`);
      this.context.stdout.write(`  ${colors.hint("directory:")} it now appears in every a2a caller's discover_workers as a callee. Hosted agents are callees only — no caller connection.\n`);
      return 0;
    }
    this.context.stdout.write(`${colors.success("✓")} ${colors.field(this.worker)} is a2a-enabled (connection ${colors.field(result.connectionName ?? "")}, agent version ${result.foundryVersion}).\n`);
    this.context.stdout.write(`  ${colors.hint("directory:")} it now appears in every a2a worker's discover_workers, and can delegate via invoke_worker.\n`);
    return 0;
  }

  private resolvePersonaPath(): string {
    if (typeof this.persona === "string" && this.persona.length > 0) {
      if (this.persona.includes("/") || this.persona.endsWith(".md")) return this.persona;
      return path.join(resolveRepoRoot(), "personas", this.persona, "persona.md");
    }
    const yaml = readAgentYaml(this.worker);
    if (yaml?.personaPath) {
      return yaml.personaPath.startsWith("/") ? yaml.personaPath : path.join(resolveRepoRoot(), yaml.personaPath);
    }
    throw new LocalCliError({ code: "A2A_NO_PERSONA", message: `Could not resolve a persona for '${this.worker}'. Pass --persona <name>.` });
  }
}
